import { Observable, Subscription } from 'rxjs';
import { concatMap, finalize } from 'rxjs/operators';
import Store from '@dojo/framework/stores/Store';

import { logQuiz } from 'logger';
import timesync from 'timesync';

import { Queue, LinkedList } from 'collections';
import { ProgressionSvc, StartQuiz, Question, QuizMeta } from '../interfaces';
import { setCursor, setQuestion, setStopLoading, setLoaded } from './state';
import { delay as waitFor } from './delay';

const ISCOMPLETE = 1;
const ISCORRECT = 2;
const NE_ANSWER = 3;
const STEP = 17;

interface GetQuestionListParms {
	qrlocator: string;
	select: { start: number; count: number; resultid?: string };
}

interface GetQuestionQueueParms {
	qrlocator: string;
	select: { begin: number; end: number };
}

const fixup = (question: Question, foraudit: boolean = false): Question => {
	const chosen = question.distractororder.filter((key) => {
		return question.distractordata[key].chosen;
	});
	if (chosen.length > 0) {
		const value = chosen[0];
		const idx = question.distractororder.indexOf(+value);
		question.answer = `${value}`;
		question.flags[ISCOMPLETE] = true;
		if (foraudit) {
			const iscorrect = question.flags[idx + 4];
			question.marked = !iscorrect;
		}
	} else if (question.questiontype === 'NumericEntry') {
		const value =
			question.answer === undefined || question.answer === null ? question.flags[NE_ANSWER] : question.answer;
		if (value !== undefined && value !== null) {
			question.answer = `${value}`;
			question.flags[ISCOMPLETE] = true;
		} else {
			question.flags[ISCOMPLETE] = false;
		}
		if (foraudit) {
			question.marked = !question.flags[ISCORRECT];
		}
	} else {
		question.flags[ISCOMPLETE] = false;
		if (foraudit) {
			question.marked = true;
		}
	}
	return question;
};

export class QueueLoader {
	private _queue: Queue<Question> | undefined;
	private _list: LinkedList<Question> | undefined;
	private svc: ProgressionSvc;
	private numberofquestions: number;
	private firstquestion: number;
	private instanceId: string;
	private loader: Subscription | undefined;
	public stoppedloading: boolean;
	private isrunning: boolean;
	private useQueue: boolean;
	private resultid: string | undefined;
	private quizstore: Store<QuizMeta>;

	constructor({
		svc,
		numberofquestions,
		instanceId,
		useQueue,
		quizstore,
		resultid,
		firstquestion = 0
	}: {
		svc: ProgressionSvc;
		numberofquestions: number;
		instanceId: string;
		useQueue: boolean;
		quizstore: Store<QuizMeta>;
		firstquestion?: number;
		resultid?: string;
	}) {
		if (useQueue) {
			this._queue = new Queue<Question>();
		} else {
			this._list = new LinkedList<Question>();
		}
		this.svc = svc;
		this.numberofquestions = numberofquestions;
		this.firstquestion = firstquestion;
		this.instanceId = instanceId;
		this.stoppedloading = false;
		this.isrunning = false;
		this.useQueue = useQueue;
		this.quizstore = quizstore;
		this.resultid = resultid;
		console.debug(`QueueLoader#constructor`);
	}

	public get queue(): Queue<Question> | LinkedList<Question> {
		if (this.useQueue) {
			return this._queue!;
		} else {
			return this._list!;
		}
	}

	protected load_list(startquiz: StartQuiz, isaudit: boolean = false): void {
		const { svc, firstquestion, numberofquestions, resultid, quizstore, _list } = this;
		const queue = _list!;
		setLoaded(quizstore)({ loaded: false });
		const totalquestions = numberofquestions + firstquestion;
		this.isrunning = totalquestions > 0;
		if (!this.isrunning) {
			return;
		}
		setStopLoading(quizstore)({
			stopLoading: () => {
				this.stopLoading();
			}
		});
		queue.clear();
		const { cursor: startcursor } = startquiz;

		let loadDelay = 1000;
		const fullsteps = Math.floor(numberofquestions / STEP);
		const countremaining = numberofquestions % STEP;

		// generate a stream of parameters for get_questions
		const parmstream = new Observable<GetQuestionListParms>((observer) => {
			let cursor = firstquestion;
			logQuiz.debug(
				() =>
					`startcursor: ${startcursor}; cursor: ${cursor}; fullsteps: ${fullsteps}; remaining: ${countremaining}`
			);
			for (let steps = 0; steps < fullsteps; ++steps) {
				const parms: GetQuestionListParms = {
					qrlocator: '',
					select: {
						start: cursor,
						count: STEP,
						resultid
					}
				};
				observer.next(parms);
				cursor += STEP;
			}
			if (countremaining > 0) {
				const parms: GetQuestionListParms = {
					qrlocator: '',
					select: {
						start: cursor,
						count: countremaining,
						resultid
					}
				};
				observer.next(parms);
				cursor += countremaining;
			}
			observer.complete();
		});

		let firstQuestion = () => {
			if (queue.length > startcursor - firstquestion) {
				const question: Question = queue.elementAtIndex(startcursor - firstquestion)!;
				if (!question.starttime) {
					question.starttime = timesync.now().toJSON();
				}
				question.status = isaudit ? 'feedback' : 'question';
				setCursor(quizstore)({ cursor: startcursor - firstquestion });
				setQuestion(quizstore)({ question });
				loadDelay = 2000;
				firstQuestion = () => undefined;
			}
		};

		this.loader = parmstream
			.pipe(
				concatMap(async (parms) => {
					await waitFor(loadDelay);
					logQuiz.debug(() => `get_questions(${JSON.stringify(parms)})`);
					if (!this.stoppedloading) {
						return svc.get_questions(parms);
					}
					return [];
				}),
				finalize(() => {
					setLoaded(quizstore)({ loaded: true });
					setStopLoading(quizstore)({ stopLoading: () => undefined });
				})
			)
			.subscribe((questions) => {
				if (this.stoppedloading) {
					return;
				}
				questions.forEach((question) => {
					const { cursor } = question;
					if (cursor - firstquestion === queue.length) {
						queue.add(fixup(question, isaudit));
					}
				});
				firstQuestion();
			});
	}

	protected load_queue(startquiz: StartQuiz, isaudit: boolean = false): void {
		const { svc, numberofquestions, quizstore, _queue } = this;
		const queue = _queue!;

		this.isrunning = numberofquestions > 0;
		if (!this.isrunning) {
			return;
		}

		logQuiz.debug(() => `started QueueLoader#load_questions for ${this.instanceId}`);
		this.stoppedloading = false;
		queue.clear();
		setStopLoading(quizstore)({
			stopLoading: () => {
				this.stopLoading();
			}
		});

		let { cursor } = startquiz;
		const { status } = startquiz;
		const startcursor = cursor;

		// generate a stream of parameters for get_questions
		const parmstream = new Observable<GetQuestionQueueParms>((observer) => {
			while (cursor < numberofquestions && !this.stoppedloading) {
				const parms: GetQuestionQueueParms = {
					qrlocator: '',
					select: {
						begin: cursor,
						end: Math.min(cursor + 6, numberofquestions)
					}
				};
				observer.next(parms);
				cursor += 7;
			}
			observer.complete();
		});

		this.loader = parmstream
			.pipe(
				// delay(1000),
				concatMap(async (parms) => {
					await waitFor(1000);
					logQuiz.debug(() => `get_questions(${JSON.stringify(parms)})`);
					if (!this.stoppedloading) {
						const questions = await svc.get_questions(parms);
						return questions;
					}
					return [];
				}),
				finalize(() => {
					setStopLoading(quizstore)({ stopLoading: undefined });
				})
			)
			.subscribe((questions: Question[]) => {
				if (this.stoppedloading) {
					return;
				}
				questions.forEach((question) => {
					if (question.cursor === startcursor) {
						setCursor(quizstore)({ cursor: 0 });
						question.starttime = timesync.now().toJSON();
						question.status = status;
						const fixedQuestion =
							question.questiontype === 'MCQuestion' ? fixup(question, isaudit) : question;
						setQuestion(quizstore)({ question: fixedQuestion });
					} else {
						queue.enqueue(question);
					}
				});
			});
	}

	public load_questions(startquiz: StartQuiz, isaudit = false): void {
		if (this.useQueue) {
			this.load_queue(startquiz, isaudit);
		} else {
			this.load_list(startquiz, isaudit);
		}
	}

	public stopLoading(): void {
		logQuiz.debug(() => `QuestionQueue#stopLoading for ${this.instanceId}`);
		const { quizstore } = this;
		if (this.stoppedloading) {
			return;
		}
		this.stoppedloading = true;
		this.loader && this.loader.unsubscribe();
		this.loader = undefined;
		this.isrunning = false;
		setQuestion(quizstore)({ question: undefined });
		this.queue.clear();
		setStopLoading(quizstore)({ stopLoading: undefined });
	}
}

export const allqueues = new Map<string, QueueLoader>();

export function clearLoaders(): void {
	allqueues.forEach((loader) => {
		loader.stopLoading();
	});
	allqueues.clear();
}

export const allmpequeues = new Map<string, QueueLoader[]>();

export function clearMPELoaders(): void {
	allmpequeues.forEach((loaders) => {
		loaders.forEach((loader) => loader.stopLoading());
	});
	allmpequeues.clear();
}
